import { Setting } from "../../../../types/entities";

/**
 * How a setting's current value should be summarised in the settings index.
 *
 * The index previously showed nothing but a label, so finding out whether email
 * sending was on meant opening the setting. Showing the value on the index is
 * the point of this helper — but a settings page is exactly where credentials
 * live, so anything that looks like a secret is masked rather than printed.
 */
export type SettingPreviewTone = 'value' | 'muted' | 'on' | 'off' | 'secret';

export type SettingPreview = {
    text: string,
    tone: SettingPreviewTone,
    /**
     * True when the text is a short fixed token — "Yes", "8 pairs", "Email
     * template", a mask. Those should never be ellipsised; only free-form
     * values a person typed are allowed to truncate.
     */
    fixed: boolean,
};

const SECRET_PATTERN: RegExp = /api[_ ]?key|secret|password|passphrase|token|signature|credential|private[_ ]?key|(^|[_ ])key([_ ]|$)/i;

/**
 * True when a setting's value should never be printed on the index page.
 *
 * The name test alone over-reaches: "Admin Customer Create Auto Generated
 * Password Character Length" holds the number 12, and masking that tells the
 * reader nothing. A credential is never a bare number, so numeric values are
 * always shown.
 */
export function isSecretSetting(setting: Setting, value?: string): boolean {
    if (value !== undefined && /^\d+$/.test(value.trim())) {
        return false;
    }
    return SECRET_PATTERN.test(setting.code ?? '') || SECRET_PATTERN.test(setting.label ?? '');
}

function count(value: unknown): number {
    return Array.isArray(value) ? value.length : 0;
}

function plural(n: number, word: string): string {
    return `${n} ${word}${n === 1 ? '' : 's'}`;
}

export function previewSetting(setting: Setting): SettingPreview {
    const value: any = setting.value;

    switch (setting.type) {
        case 'yes/no':
            return (value === true || value === 'true')
                ? { text: 'Yes', tone: 'on', fixed: true }
                : { text: 'No', tone: 'off', fixed: true };

        // 'multiple' is what the create form writes; 'multi' is what the edit
        // form reads. Both are accepted here so neither renders as blank.
        case 'multi':
        case 'multiple': {
            const n: number = count(value);
            return n === 0
                ? { text: 'None', tone: 'muted', fixed: true }
                : { text: plural(n, 'option'), tone: 'value', fixed: true };
        }

        case 'key/value': {
            const n: number = count(value);
            return n === 0
                ? { text: 'None', tone: 'muted', fixed: true }
                : { text: plural(n, 'pair'), tone: 'value', fixed: true };
        }

        case 'system_email_template':
            return { text: 'Email template', tone: 'value', fixed: true };

        case 'system_invoice_template':
            return { text: 'HTML template', tone: 'value', fixed: true };

        case 'text':
        case 'textarea':
        default: {
            const text: string = typeof value === 'string' ? value.trim() : '';

            if (text.length === 0) {
                return { text: 'Not set', tone: 'muted', fixed: true };
            }

            if (isSecretSetting(setting, text)) {
                return { text: '••••••••', tone: 'secret', fixed: true };
            }

            return { text, tone: 'value', fixed: false };
        }
    }
}
